import {createContext, ReactNode, useEffect, useState, useRef, useCallback} from 'react'
import {safeLocalStorage} from "../util/safeStorage"
import {useLocation, useNavigate} from 'react-router-dom'
import {AxiosError} from 'axios'
import {createSessionCookies, getToken, removeSessionCookies} from "../util/tokenCookies"
import {authApi, getCandidateDetails, getUserApi, getUserDetails, loginUserApi, signupUserApi} from "../services/api"
import {setAuthorizationHeader} from "../services/interceptors"
import {IClientProfile, ICandidate} from "../services/types"
import {useMutation, useQuery, useQueryClient} from "@tanstack/react-query"
import {canAccessFeature, shouldShowInMenu} from "../config/features"
import {useSubscriptionEndDate} from "../hooks/useSubscriptionEndDate";

type Props = {
    children: ReactNode
}

export type User = {
    id: number
    email: string
    role: string
    plan_end_date: number
    created_at: number
    name: string
    sessionId: string
    recreated_at: string
    pd_person_id: string
    pd_deal_id: string
    client_id: number
    users_id_label: string
    client_role: string
    hash_md5: string
    last_authtoken: number
    last_logout: string
    plan_type: string
    outreaches_allowed: string
    subscriptions_id: number
    outreaches_current_month: number
    status: string
    phone: string
    plan_label: string
    outreaches_allowed__monthly: number
    sendgrid_id: string
    candidate_id: number
    display_plan_label: string
    jobs_allowed: string
    jobs_current_month: number
    jobs_allowed_monthly: number
    mode: string
    key: string
    magic_link: {
        token: string
        expiration: number
        used: boolean
    },
    _candidate?: IClientProfile | ICandidate
}

export type SignInCredentials = {
    email: string
    password: string
}

export type SignUpCredentials = {
    email: string
    password: string
    telephone: string
}

export type AuthContextData = {
    user?: User
    isAuthenticated: boolean
    loadingUserData: boolean
    signIn: (credentials: SignInCredentials | { token: string }) => Promise<void | AxiosError>
    signUp: (credentials: SignUpCredentials) => Promise<void | AxiosError>
    signOut: () => void
    error: string | null
    setError: (st: string) => void
    setUser: (user: User) => void
    canAccess: (feature: string) => boolean
    shouldShowFeatureInMenu: (feature: string) => boolean
}

export const AuthContext = createContext({} as AuthContextData)

export const AuthProvider = ({children}: Props) => {
    const [user, setUser] = useState<User | undefined>();
    const [error, setError] = useState<string | null>(null);
    const navigate = useNavigate();
    const {pathname} = useLocation();
    const queryClient = useQueryClient();

    const token = getToken();
    const isAuthenticated = Boolean(token);

    const {
        data: userData,
        isLoading: loadingUserData,
    } = useQuery({
        queryKey: ['user'],
        queryFn: async () => {
            if (!token) return null

            try {
                setAuthorizationHeader({request: authApi.defaults, token});
                const response = await getUserApi();

                if (response) {
                    const {id, plan_label, plan_end_date, plan_type} = response;
                    const moreDetails = await getUserDetails(id)
                    const candidateDetails = await getCandidateDetails()

                    const userDetails = {
                        id,
                        plan_label,
                        plan_type,
                        plan_end_date,
                        ...moreDetails,
                        _candidate: candidateDetails
                    }
                    setUser(userDetails as never);
                }

                return null;
            } catch (err) {
                // If user fetch fails (for example token expired/invalid), clear session
                // to avoid a state where token exists but user is undefined which
                // causes redirect loops in route guards.
                removeSessionCookies();
                setUser(undefined);
                
                throw err;
            }
        },
        enabled: !!token,
        retry: false,
    });

    useEffect(() => {
        if (userData) {
            setUser(userData);
        }
    }, [userData]);

    const redirectAttempted = useRef(false);

    useEffect(() => {
        if (isAuthenticated && user && !redirectAttempted.current) {
            const savedPath = safeLocalStorage.getItem('appPredefinedRedirectPath');

            redirectAttempted.current = true;

            if (savedPath && savedPath !== pathname && savedPath !== '/') {
                safeLocalStorage.removeItem('appPredefinedRedirectPath');

                setTimeout(() => {
                    navigate(savedPath);
                }, 50);
            } else if (savedPath) {
                safeLocalStorage.removeItem('appPredefinedRedirectPath');
            }
        }
    }, [isAuthenticated, user, navigate, pathname]);

    useEffect(() => {
        redirectAttempted.current = false;

        if (!isAuthenticated) {
            safeLocalStorage.removeItem('appPredefinedRedirectPath');
        }
    }, [isAuthenticated]);
    const signOut = useCallback(() => {
        safeLocalStorage.removeItem('appRedirectPath')
        safeLocalStorage.removeItem('appPredefinedRedirectPath')
        removeSessionCookies();
        setUser(undefined);
        queryClient.clear();
        // Navigate directly to the login page to avoid hitting the root PrivateRoute
        // which could re-trigger redirects.
        navigate('/login', { replace: true });
    }, [navigate, queryClient]);

    // Listen for global logout events dispatched by interceptors on 401
    useEffect(() => {
        const handleExternalLogout = () => {
            signOut();
        };

        window.addEventListener('silvertalent:logout', handleExternalLogout as EventListener);
        return () => window.removeEventListener('silvertalent:logout', handleExternalLogout as EventListener);
    }, [signOut]);

    useEffect(() => {
        const savedPath = safeLocalStorage.getItem('appPredefinedRedirectPath');

        if (savedPath && savedPath === pathname) {
            safeLocalStorage.removeItem('appPredefinedRedirectPath');
        }
    }, [pathname]);

    const signInMutation = useMutation({
        mutationFn: async (params: SignInCredentials | { token: string }) => {
            let authToken: string;

            if ('email' in params && 'password' in params) {
                const {email, password} = params;
                const response = await loginUserApi(email, password);
                authToken = response.authToken;
            } else {
                authToken = params.token;
            }

            createSessionCookies({authToken});
            setAuthorizationHeader({request: authApi.defaults, token: authToken});
            return authToken;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({queryKey: ['user']});
            setError(null)
        },
    });

    const signUpMutation = useMutation({
        mutationFn: async ({email, password, telephone}: SignUpCredentials) => {
            const response = await signupUserApi(email, password, telephone);
            return response.authToken;
        },
        onSuccess: (authToken) => {
            createSessionCookies({authToken});
            setAuthorizationHeader({request: authApi.defaults, token: authToken});
            queryClient.invalidateQueries({queryKey: ['user']});
        },
    });

    const signIn = async (params: SignInCredentials | { token: string }) => {
        try {
            await signInMutation.mutateAsync(params);
        } catch (error) {
            throw error as AxiosError;
        }
    };

    const signUp = async (params: SignUpCredentials) => {
        try {
            await signUpMutation.mutateAsync(params);
        } catch (error) {
            throw error as AxiosError;
        }
    };

    useEffect(() => {
        if (!token) {
            removeSessionCookies();
            setUser(undefined);
        }
    }, [navigate, pathname, token]);

    // Get subscription end date for plan-based access control
    const { endDate } = useSubscriptionEndDate();

    // Check if subscription is active (end date is valid and in the future)
    const hasActiveSubscription = endDate && !isNaN(endDate.getTime()) && endDate > new Date();

    const canAccess = (feature: string): boolean => {
        return canAccessFeature(feature, hasActiveSubscription ? 'paid' : 'free');
    };

    const shouldShowFeatureInMenu = (feature: string): boolean => {
        return shouldShowInMenu(feature, hasActiveSubscription ? 'paid' : 'free');
    };

    const contextValue: AuthContextData = {
        isAuthenticated,
        user,
        setUser,
        loadingUserData,
        signIn,
        signOut,
        signUp,
        setError,
        error,
        canAccess,
        shouldShowFeatureInMenu
    };

    return (
        <AuthContext.Provider value={contextValue}>
            {children}
        </AuthContext.Provider>
    );
};
